/**
 * Three states for "the cart is not at rest", split out of the drawer's single `busy`
 * boolean.
 *
 * The old boolean conflated two genuinely different situations, and the drawer told the
 * visitor the pessimistic story for both:
 *
 *   unsettled — a write is in flight whose outcome NOBODY knows yet. Saying "Updating
 *               your cart..." and refusing the checkout click is honest here.
 *   settling  — every write has come back Saleor-CONFIRMED; only the rendered display
 *               props are behind, because they arrive via a full-route `router.refresh()`.
 *               The line is provably in the cart. Claiming work is still in progress, and
 *               blocking checkout on it, is not honest — it is just the UI waiting on its
 *               own re-render.
 *
 * The distinction is load-bearing for correctness, not only for tone: during `settling`
 * the drawer today prints the PRE-ADD subtotal and discount as confident numbers
 * (`resolveCommerceSummary` passes them through its early return verbatim), i.e. totals
 * that omit the item the visitor just added.
 *
 * Deliberately pure and React-free: the drawer, `/cart`'s own banner
 * (`cart-mutation-snapshot.tsx`), the mobile checkout bar and the quantity controls each
 * hand-roll this same expression today, and they should converge here one at a time.
 */

type CartBusyKind = "ready" | "unsettled" | "settling";

/** Only the fields this decision needs; keeps the helper free of the drawer's types. */
interface BusyStateLine {
	status: "pending" | "confirmed" | "failed";
}

export interface CartBusyStateInput {
	/**
	 * A quantity/delete/upgrade write is running, or the provider holds a mutation lock.
	 * Always `unsettled`: unlike an add, these carry no client-side confirmation.
	 */
	mutationInFlight: boolean;
	transientLines: readonly BusyStateLine[];
}

export interface CartBusyState {
	kind: CartBusyKind;
}

/**
 * A failed row is excluded from both states. It stays on screen with its retry button,
 * but it is not work in progress, and the cart around it is at rest. This mirrors the
 * `status !== "failed"` filter the drawer's boolean already used.
 */
export function resolveCartBusyState({
	mutationInFlight,
	transientLines,
}: CartBusyStateInput): CartBusyState {
	const live = transientLines.filter((line) => line.status !== "failed");

	if (mutationInFlight || live.some((line) => line.status === "pending")) {
		return { kind: "unsettled" };
	}
	return { kind: live.length > 0 ? "settling" : "ready" };
}

/**
 * Exactly the drawer's historical `busy` boolean, expressed over the new states.
 *
 * Keeping this as a named export (rather than inlining `kind !== "ready"` at the call
 * site) is what lets `cart-busy-state.test.ts` assert the equivalence as a property over
 * a table of inputs — the guarantee that introducing the tri-state moved no amount and
 * changed no existing gate until a caller deliberately opts into the finer distinction.
 */
export function isCartBusy(state: CartBusyState): boolean {
	return state.kind !== "ready";
}
